import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IBaseItem } from '../../common-items/BaseItem/base-item-form.component';
import {
  iHasCaptionLeft,
  IHasCaptionLeft,
  iHasInputColorControls,
  IHasInputColorControls,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';

import { MatIcon } from '@angular/material/icon';
import { getPropsFrom, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import {
  BASE_TEXT_COLOR_VARIANTS,
  extractColorProperties,
  getColors,
  getInputStyle,
  getTextColorsPropertiesStyle
} from '../../../../utils/colors';
import { DEFAULT_INITIAL_DATE, formatUtcSeconds } from '../../../../utils/dates';
import { getOptimalFirstColumnWidth } from '../../../../utils/getOptimalWidth';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { DARK_THEME_NAME } from '../../types/editor-settings';

interface TimeInputProperties {
  text: string;
  style: string;
  selectedDateTime: string;
  captionWidth: number;
  inputType: string;
}

@Component({
  selector: 'lfe-time-input-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './time-input-item-form.component.html',
  standalone: true,
  styleUrl: './time-input-item-form.component.css'
})
export class TimeInputItemFormComponent
  extends iHasInputColorControls(iHasCaptionLeft(InteractiveItemComponent<TimeInputProperties>))
  implements OnInit, IBaseItem<TimeInputProperties>, IHasCaptionLeft, IHasInputColorControls
{
  readonly translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;
  properties: InteractiveItemProperties & TimeInputProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: '',
    selectedDateTime: '',
    captionWidth: 0,
    inputType: 'time'
  };
  disabled = false;

  readonly updateFirstColumnWidthRequired = output<{ id: string; width: number }>();
  firstColumnWidth = computed(
    () => this.sharedFirstColumnWidths()[this.firstSiblingItemId()] || getOptimalFirstColumnWidth(this.properties.text)
  );

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `time_input_${this.formControlId}`);

  currentTextColor = signal('');

  inputStyle = computed(() => getInputStyle(this.currentInputBgColor(), this.currentInputTextColor()));

  inputClass = computed(() => {
    const isDarkMode = this.settings().theme === DARK_THEME_NAME;
    const useInvertedCalendarIndicator = isDarkMode
      ? this.currentInputTextColor() === BASE_TEXT_COLOR_VARIANTS.black
      : this.currentInputTextColor() === BASE_TEXT_COLOR_VARIANTS.white;
    return { 'inverted-calendar-indicator': useInvertedCalendarIndicator };
  });

  getCaptionStyle(): { [key: string]: string } | null {
    if (!this.properties.text) {
      return null;
    }

    const textStyle = getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor());

    return {
      width: `${this.firstColumnWidth()}px`,
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'white-space': 'nowrap',
      display: 'inline-block',
      ...textStyle
    };
  }

  getCaptionTitle(): string | null {
    if (!this.properties.text) {
      return null;
    }
    if (this.firstColumnWidth() <= 0) {
      return null;
    }
    return this.properties.text;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    const initialValue = this.parseStringToTimeInput(this.properties.selectedDateTime);
    this.formControl = new FormControl(initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    this.updateFirstColumnWidthRequired.emit({
      id: this.firstSiblingItemId(),
      width: this.firstColumnWidth()
    });
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & TimeInputProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const selectedDateTime = props['SelectedDateTime'] || '';
    const captionWidth = Number(props['CaptionWidth'] || 0);
    const inputType = props['Type'] || 'time';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));

    this.currentTextColor.set(textColor);

    const inputColor = this.initInputColorProperties(props);

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      resultValue,
      resultUser,
      resultTime,
      text,
      textBgColor,
      inputColor,
      style,
      selectedDateTime,
      captionWidth,
      inputType,
      isGenericRepeatItem
    };
  }

  private parseStringToTimeInput(value: string): string | null {
    if (!value || value === DEFAULT_INITIAL_DATE) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return this.formatTimeForInput(date);
  }

  /**
   * Local wall-clock time of the given instant, used both for the "now" button and for reading
   * SelectedDateTime back.
   *
   * WORKAROUND - reading it locally is only correct because formatTimeForStorage writes a real UTC
   * instant, matching the desktop app. Once that is fixed to store the picked time verbatim, this has
   * to read the UTC fields instead:
   *
   * const hours = date.getUTCHours().toString().padStart(2, '0');
   * const minutes = date.getUTCMinutes().toString().padStart(2, '0');
   * const seconds = date.getUTCSeconds().toString().padStart(2, '0');
   */
  private formatTimeForInput(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  /**
   * WORKAROUND - matches the desktop app, which stores SelectedDateTime as a real UTC instant on the
   * current date. So 14:30 in UTC+2 is written as <today>T12:30:00.000Z, and reading it back has to
   * convert to local again (see formatTimeFromStorage).
   *
   * That is arguably wrong for a time-only field: the item means a wall-clock time, so the same form
   * shows a different time to a colleague in another timezone, and the value silently shifts across a
   * DST boundary. The correct behaviour is to keep the picked time verbatim and anchor it to
   * 1000-01-01 like DEFAULT_INITIAL_DATE does - kept below, to be restored once the desktop app is
   * fixed:
   *
   * const [hours, minutes, seconds] = inputValue.split(':');
   * const date = new Date();
   * date.setUTCFullYear(1000, 0, 1);
   * date.setUTCHours(parseInt(hours) || 0, parseInt(minutes) || 0, parseInt(seconds) || 0, 0);
   * return date.toISOString();
   *
   * Note the anchor cannot stay at 1000-01-01 while converting: year 1000 predates standard time, so
   * the runtime applies the historical local mean time offset (+53 min for Europe/Berlin) and 14:30
   * would become 13:37:17Z, with early times slipping back to 0999-12-31.
   */
  private formatTimeForStorage(inputValue: string): string {
    if (!inputValue) return DEFAULT_INITIAL_DATE;

    const [hours, minutes, seconds] = inputValue.split(':');
    const date = new Date();
    date.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, parseInt(seconds) || 0, 0);

    return formatUtcSeconds(date);
  }

  /**
   * ResultValue holds the readable time, not the ISO timestamp that goes into SelectedDateTime -
   * same split as DateInputItem, which pairs an ISO SelectedDateTime with a "dd.MM.yyyy" ResultValue.
   */
  private formatTimeForResultValue(inputValue: string): string {
    if (!inputValue) {
      return '';
    }
    const [hours, minutes, seconds] = inputValue.split(':');
    const pad = (part: string | undefined): string => (parseInt(part ?? '0') || 0).toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  onSetNowClick(event: Event): void {
    event.preventDefault();
    const now = new Date();
    const timeString = this.formatTimeForInput(now);
    this.formControl?.setValue(timeString);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.selectedDateTime = this.formatTimeForStorage(timeString);

    this.properties.resultValue = this.formatTimeForResultValue(timeString);
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = formatUtcSeconds(new Date());
  }

  onClearTimeClick(event: Event): void {
    event.preventDefault();
    this.formControl?.setValue(null);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.selectedDateTime = DEFAULT_INITIAL_DATE;

    this.properties.resultValue = '';
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = formatUtcSeconds(new Date());
  }

  onTimeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value) {
      this.properties.selectedDateTime = this.formatTimeForStorage(value);
    } else {
      this.properties.selectedDateTime = DEFAULT_INITIAL_DATE;
    }

    this.properties.resultValue = this.formatTimeForResultValue(value);
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = formatUtcSeconds(new Date());
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    this.handleInputColorStorage(element);

    if (this.properties.selectedDateTime !== DEFAULT_INITIAL_DATE) {
      upsertChildren(element, { SelectedDateTime: this.properties.selectedDateTime });
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }
}
